import { logError } from 'lib/observability';
import { enforcePromiseTimeout } from 'lib/speechify/adaptors/sqlite/shared';

import {
  IndexedDBStoreInstance,
  LocalStorageStoreInstance,
  NonPersistentStoreInstance,
  StoreInstance,
  isIndexedDbStoreInstance,
  isLocalStorageStoreInstance,
  isNonPersistentStoreInstance
} from './types';

type StoreRegistryItem<StoreInstanceType extends StoreInstance<unknown>> = {
  name: Set<string>;
  instance: StoreInstanceType[];
};

const STORE_REGISTRY: {
  nonPersistent: StoreRegistryItem<NonPersistentStoreInstance<unknown>>;
  indexedDB: StoreRegistryItem<IndexedDBStoreInstance<unknown>>;
  localStorage: StoreRegistryItem<LocalStorageStoreInstance<unknown>>;
} = {
  nonPersistent: {
    name: new Set(),
    instance: []
  },
  indexedDB: {
    name: new Set(),
    instance: []
  },
  localStorage: {
    name: new Set(),
    instance: []
  }
};

export const registerStore = (store: StoreInstance<unknown>) => {
  if (isNonPersistentStoreInstance(store)) {
    STORE_REGISTRY.nonPersistent.instance.push(store);
  } else if (isIndexedDbStoreInstance(store)) {
    if (STORE_REGISTRY.indexedDB.name.has(store.storageName)) {
      throw new Error(`Store with storage name ${store.storageName} already registered`);
    }
    STORE_REGISTRY.indexedDB.name.add(store.storageName);
    STORE_REGISTRY.indexedDB.instance.push(store);
  } else if (isLocalStorageStoreInstance(store)) {
    if (STORE_REGISTRY.localStorage.name.has(store.storageName)) {
      throw new Error(`Store with storage name ${store.storageName} already registered`);
    }
    STORE_REGISTRY.localStorage.name.add(store.storageName);
    STORE_REGISTRY.localStorage.instance.push(store);
  }
};

export const unregisterStore = (store: StoreInstance<unknown>) => {
  if (isNonPersistentStoreInstance(store)) {
    STORE_REGISTRY.nonPersistent.instance = STORE_REGISTRY.nonPersistent.instance.filter(instance => instance !== store);
  } else if (isIndexedDbStoreInstance(store)) {
    STORE_REGISTRY.indexedDB.instance = STORE_REGISTRY.indexedDB.instance.filter(instance => instance !== store);
    STORE_REGISTRY.indexedDB.name.delete(store.name);
  } else if (isLocalStorageStoreInstance(store)) {
    STORE_REGISTRY.localStorage.instance = STORE_REGISTRY.localStorage.instance.filter(instance => instance !== store);
    STORE_REGISTRY.localStorage.name.delete(store.name);
  }
};

/**
 * Resets the state of all registered stores to their initial state.
 * For persistent stores, this might involve clearing the stored data.
 */
export const resetAllStores = async (): Promise<void> => {
  const nonPersistentStores = STORE_REGISTRY.nonPersistent.instance;
  const indexedDbStores = STORE_REGISTRY.indexedDB.instance;
  const localStorageStores = STORE_REGISTRY.localStorage.instance;
  const allStores = [...nonPersistentStores, ...indexedDbStores, ...localStorageStores];

  const resetPromises = allStores.map(store => (store.reset ? store.reset() : Promise.resolve()));

  // Use Promise.allSettled to ensure all resets are attempted even if one fails
  const results = await Promise.allSettled(resetPromises);
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      // Attempt to get store info for logging, might not be easily available without iterating registry again
      logError(new Error(`Store reset failed (index ${index}): ${result.reason}`));
    }
  });
};

/**
 * Destroys persistent stores: Removes object store (IndexedDB), removes data (LocalStorage),
 * and attempts to delete IndexedDB databases.
 * Should be used on logout or when complete cleanup is needed.
 */
export const destroyPersistentStores = async (): Promise<void> => {
  const indexedDbStores = STORE_REGISTRY.indexedDB.instance;
  const localStorageStores = STORE_REGISTRY.localStorage.instance;

  // 1. Close all IndexedDB connections first
  const closePromises = indexedDbStores.map(store => store.purgePersistentLayer());
  const closeResults = await Promise.allSettled(closePromises);
  closeResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      logError(new Error(`IndexedDB store connection close failed (${indexedDbStores[index]?.storageName || 'unknown'}):`, result.reason));
    }
  });

  // 2. Clear LocalStorage items (destroy calls reset which removes the item)
  const clearLocalStoragePromises = localStorageStores.map(store => store.purgePersistentLayer());
  const clearLocalStorageResults = await Promise.allSettled(clearLocalStoragePromises);
  clearLocalStorageResults.forEach((result, index) => {
    if (result.status === 'rejected') {
      logError(new Error(`LocalStorage clear failed (${localStorageStores[index]?.storageName || 'unknown'}):`, result.reason));
    }
  });
};

export const TEST_ONLY_resetRegistry = () => {
  STORE_REGISTRY.indexedDB.instance = [];
  STORE_REGISTRY.indexedDB.name = new Set();
  STORE_REGISTRY.localStorage.instance = [];
  STORE_REGISTRY.localStorage.name = new Set();
  STORE_REGISTRY.nonPersistent.instance = [];
};

/**
 * Waits for all persistent stores to complete their initial hydration.
 * This function should be called during app initialization to ensure all stores
 * are properly hydrated before rendering the app.
 */
export const awaitStoresHydration = async (): Promise<void> => {
  const indexedDbStores = STORE_REGISTRY.indexedDB.instance;
  const localStorageStores = STORE_REGISTRY.localStorage.instance;
  const persistentStores = [...indexedDbStores, ...localStorageStores];

  if (persistentStores.length === 0) {
    return;
  }

  // Import profiling store dynamically to avoid circular dependencies
  const { profilingStoreActions } = await import('modules/profiling/profilingStore');
  const { MeasurementKey } = await import('modules/profiling/measurementTypes');

  const hydrationPromises = persistentStores
    .filter(store => {
      // Only hydrate stores that have hydrateAtLoadTime set to true or undefined (default: true)
      return store.options?.hydrateAtLoadTime !== false;
    })
    .map(async store => {
      const storeName = 'storageName' in store ? store.storageName : 'unknown';

      return profilingStoreActions.measureAction(
        async () => {
          if ('waitForInitialHydration' in store) {
            await store.waitForInitialHydration();
          }
        },
        {
          measurementKey: MeasurementKey.zustandIdbHydration,
          identifier: {},
          context: {
            storeName
          }
        }
      );
    });

  try {
    await profilingStoreActions.measureAction(async () => enforcePromiseTimeout(Promise.all(hydrationPromises), 200), {
      measurementKey: MeasurementKey.zustandStoresHydration,
      identifier: {},
      context: {
        storeName: 'unknown'
      }
    });
  } catch (error) {
    console.warn('Failed to hydrate some stores:', error);
    // Re-throw to allow caller to handle the error appropriately
    throw error;
  }
};
